/**
 * FamiliarisationContent Component
 * Comprehensive familiarisation management dashboard with WBC3 compliance
 * Integrates with existing conditional logic system
 */

import React, { useState, useMemo, useEffect } from 'react';
import { 
  Users, 
  CheckCircle, 
  AlertTriangle, 
  Clock,
  Search,
  FileDown
} from 'lucide-react';
import { exportIndividualFamiliarisationPDF, previewFamiliarisationPDF } from '../../../../utils/pdf/familiarisationExport';
import PDFPreviewModal from './PDFPreviewModal';
import { useStorageState } from '../../../../hooks/useStorageState';
import { useVessel } from '../../../../contexts/VesselContext';
// import { useVesselCollections } from '../../../../hooks/useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { COLLECTIONS } from '../../../../types/core';
import { useCrew } from '../../../../hooks/useCrew';
import { generateFamiliarisationWithCustomItems } from '../../../../utils/familiarisationHelpers';
import FamiliarisationModal from './FamiliarisationModal';
import FamiliarisationView from './FamiliarisationView';
import FamiliarisationSettingsModal from './FamiliarisationSettingsModal';
import FamiliarisationCustomizer from './FamiliarisationCustomizer';
import FamiliarisationCustomizerView from './FamiliarisationCustomizerView';
import FamiliarisationSidebar from './FamiliarisationSidebar';
import TemplateSelectionModal from './TemplateSelectionModal';
import Dropdown from '../../../common/Dropdown';
import { useFamiliarisationTemplates } from '../../../../hooks/useFamiliarisationTemplates';

const FamiliarisationContent = () => {
  // GPT Pattern E - Collections setup
  const { currentVesselId, getCurrentVessel } = useVessel();
  // const { vesselId, collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration
  // Phase 2.6: collections disabled — stub to prevent crash
  // FIX: Wrap in useMemo to prevent infinite loop (stable reference)
  const collections = useMemo(() => ({
    list: async () => [],
    upsert: async () => null,
    get: async () => null,
    remove: async () => null,
  }), []);
  const vesselId = currentVesselId; // Use currentVesselId from VesselContext
  const currentVessel = getCurrentVessel();
  // Note: Draft state loading temporarily disabled - completed records still load via useStorageState
  const { crew: crewMembers = [], loading: crewLoading } = useCrew();
  const { value: familiarisationRecords = [], setValue: setFamiliarisationRecords } = useStorageState('familiarisation_records', []);
  const { value: customItems = [] } = useStorageState('customFamiliarisationItems', []);
  // FT-19: Draft progress for "In Progress" status display
  const { value: draftProgress = [] } = useStorageState('familiarisation_progress', []);
  
  // State for progress data from collections
  const [progressData, setProgressData] = useState({});

  // Get active crew members only (must be defined before useEffect that uses it)
  const activeCrew = useMemo(() => {
    // Show all non-archived crew (includes both On Duty and Off Duty)
    return crewMembers.filter(crew => crew.status !== 'archived');
  }, [crewMembers]);

  // GPT Pattern E - Core functions
  const within = (range, iso) => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    const [from, to] = range || [];
    return (!from || t >= new Date(from).getTime()) &&
           (!to || t <= new Date(to).getTime());
  };

  const loadFamiliarisation = async ({ crewId, topic, dateRange }) => {
    const allTraining = await collections.list(COLLECTIONS.TRAINING);
    const completions = allTraining.filter(r =>
      (!crewId || r.crewId === crewId) &&
      (!topic  || r.topic  === topic) &&
      (!dateRange || within(dateRange, r.date))
    );

    // Derive quick stats
    const byTopic = completions.reduce((m, r) => {
      m[r.topic] = (m[r.topic] || 0) + 1; return m;
    }, {});

    return { completions, stats: { byTopic, total: completions.length } };
  };

  const acknowledgeFamiliarisation = async ({ crewId, topic, notes }) => {
    return collections.upsert(COLLECTIONS.TRAINING, {
      id: undefined,
      vesselId,
      origin: 'manual',
      crewId,
      topic,
      role: 'Crew',
      refresh: '',
      evidence: ['acknowledged'],
      notes: notes || '',
      date: new Date().toISOString(), // UTC
    });
  };

  // State declarations (must be before useEffect that depends on them)
  const [showFamiliarisationModal, setShowFamiliarisationModal] = useState(false);
  const [showFamiliarisationView, setShowFamiliarisationView] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState(null);

  // Templates hook (Phase 3)
  const {
    templates,
    getTemplatesForPosition,
    getDefaultTemplate
  } = useFamiliarisationTemplates();

  // Template selection state
  const [showTemplateSelection, setShowTemplateSelection] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [pendingCrewMember, setPendingCrewMember] = useState(null);
  const [selectedCrewRecord, setSelectedCrewRecord] = useState(null);

  // Load progress data from collections instead of localStorage
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!vesselId || !activeCrew.length) return;
      
      try {
        const newProgressData = {};
        
        for (const crew of activeCrew) {
          const progressId = `familiarisation_progress_${crew.id}`;
          const saved = await collections.get('familiarisation_progress', progressId);
          if (saved && alive) {
            newProgressData[crew.id] = saved;
          }
        }
        
        if (alive) {
          setProgressData(newProgressData);
        }
      } catch (error) {

      }
    })();
    
    return () => { alive = false; };
  }, [vesselId, activeCrew, collections, showFamiliarisationView]);

  const [familiarisationMode, setFamiliarisationMode] = useState('new'); // 'new', 'view'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'pending', 'overdue', 'completed'
  const [searchTerm, setSearchTerm] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [showCustomizerView, setShowCustomizerView] = useState(false);
  const [pdfPreview, setPdfPreview] = useState({ isOpen: false, url: null, filename: null });

  // Calculate familiarisation status for each crew member (using collections)
  const crewFamiliarisationStatus = useMemo(() => {
    return activeCrew.map(crew => {
      const latestRecord = familiarisationRecords
        .filter(record => record.crewId === crew.id)
        .sort((a, b) => new Date(b.completionDate || b.completedAt) - new Date(a.completionDate || a.completedAt))[0];

      // FT-19: Check for saved draft progress
      const crewDraft = draftProgress.find(d => d.crewId === crew.id);
      let crewProgressData = null;
      let progressPercentage = 0;

      // First check draft progress (FT-19), then fall back to old progressData (legacy)
      const savedProgress = crewDraft || progressData[crew.id];

      if (savedProgress) {
        try {
          crewProgressData = savedProgress;
          // Calculate progress percentage based on completed checklist items (not sections)
          const completedChecklists = crewProgressData.completedChecklists || {};
          const completedCount = Object.values(completedChecklists).filter(
            item => item && (item.status === 'completed' || item.status === 'na')
          ).length;

          // Use saved totalItems if available, otherwise estimate from completed count
          const totalItems = crewProgressData.totalItems || 0;

          if (totalItems > 0) {
            progressPercentage = Math.round((completedCount / totalItems) * 100);
          } else if (completedCount > 0) {
            // Fallback for legacy data: show completed count as percentage (capped at 99%)
            // This ensures we show SOME progress even if totalItems wasn't saved
            progressPercentage = Math.min(completedCount, 99);
          }
        } catch (e) {
          // Ignore parse errors
        }
      }

      let status = 'pending';
      let daysRemaining = null;
      let completedDate = null;

      // Only treat as complete if record is actually complete AND has a completion date
      const hasCompletionDate = latestRecord?.completionDate || latestRecord?.completedAt;
      const isCompleteRecord = latestRecord?.status === 'complete' || latestRecord?.status === 'completed';

      if (latestRecord && hasCompletionDate && isCompleteRecord) {
        completedDate = new Date(latestRecord.completionDate || latestRecord.completedAt);
        const daysSinceComplete = Math.floor((new Date() - completedDate) / (1000 * 60 * 60 * 24));
        daysRemaining = 365 - daysSinceComplete;

        if (daysSinceComplete > 365) {
          status = 'overdue';
        } else if (daysSinceComplete > 335) { // 30 days warning
          status = 'due_soon';
        } else {
          status = 'compliant';
        }
      } else if (latestRecord?.status === 'in_progress') {
        // Has a record but not yet complete - show as in progress
        status = 'pending';
      }

      return {
        ...crew,
        familiarisationStatus: status,
        daysRemaining,
        completedDate,
        latestRecord,
        // FT-19: Show "In Progress" if crew has a draft (not yet completed)
        hasInProgress: !!(crewDraft || progressData[crew.id]) && !isCompleteRecord,
        progressPercentage,
        lastSavedProgress: crewDraft?.lastSaved || progressData?.lastSaved
      };
    });
  }, [activeCrew, familiarisationRecords, progressData, draftProgress]);

  // Filter crew based on search and status
  const filteredCrew = useMemo(() => {
    let filtered = crewFamiliarisationStatus;
    
    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(crew => {
        switch (filterStatus) {
          case 'pending':
            return crew.familiarisationStatus === 'pending';
          case 'overdue':
            return crew.familiarisationStatus === 'overdue';
          case 'completed':
            return crew.familiarisationStatus === 'compliant' || crew.familiarisationStatus === 'due_soon';
          default:
            return true;
        }
      });
    }
    
    // Apply search filter
    // FT-181: Use crew.name (fleet_crew property) with fallback from full_name (legacy)
    if (searchTerm) {
      filtered = filtered.filter(crew => {
        const crewName = crew.name || crew.full_name || '';
        return crewName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               crew.position?.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    
    // Sort by priority: overdue → in-progress → pending → due_soon → compliant
    filtered.sort((a, b) => {
      const priority = {
        'overdue': 0,
        'in-progress': 1,
        'pending': 2,
        'due_soon': 3,
        'compliant': 4
      };
      
      const aPriority = a.hasInProgress && a.familiarisationStatus === 'pending' 
        ? priority['in-progress'] 
        : priority[a.familiarisationStatus] || 5;
      const bPriority = b.hasInProgress && b.familiarisationStatus === 'pending' 
        ? priority['in-progress'] 
        : priority[b.familiarisationStatus] || 5;
        
      return aPriority - bPriority;
    });
    
    return filtered;
  }, [crewFamiliarisationStatus, filterStatus, searchTerm]);


  // Generate vessel-specific familiarisation preview
  const vesselFamiliarisationPreview = useMemo(() => {
    if (!currentVessel) return null;
    
    try {
      const sampleCrew = { rank: 'Master', position: 'Master' };
      const familiarisationData = generateFamiliarisationWithCustomItems(currentVessel, sampleCrew, customItems);
      return familiarisationData.sections;
    } catch (error) {
      return null;
    }
  }, [currentVessel, customItems]);


  // Handle familiarisation completion
  const handleFamiliarisationComplete = async (familiarisationRecord) => {
    const updatedRecords = [...familiarisationRecords, familiarisationRecord];
    await setFamiliarisationRecords(updatedRecords);

    // Remove the in-progress record from IndexedDB now that familiarisation is complete
    // Uses collections.remove() - same pattern as FamiliarisationModal.js line 250
    if (familiarisationRecord.crewId && collections) {
      try {
        const progressId = `familiarisation_progress_${familiarisationRecord.crewId}`;
        await collections.remove('familiarisation_progress', progressId);
      } catch (error) {
        console.warn('Could not remove progress record:', error);
        // Non-critical - continue even if remove fails
      }
    }

    setShowFamiliarisationModal(false);
    setShowFamiliarisationView(false);
    setSelectedTemplate(null);
  };

  // Handle starting familiarisation - show template selection first (Phase 3)
  const handleStartFamiliarisation = (crewMember) => {
    // Get templates applicable to this crew member's position
    const applicableTemplates = getTemplatesForPosition(crewMember.position);

    // If only one template or no templates, use it directly
    if (applicableTemplates.length <= 1) {
      setSelectedTemplate(applicableTemplates[0] || null);
      setSelectedCrew(crewMember);
      setFamiliarisationMode('start');
      setShowFamiliarisationView(true);
      return;
    }

    // Show template selection modal
    setPendingCrewMember(crewMember);
    setShowTemplateSelection(true);
  };

  // Handle template selection confirmed
  const handleTemplateSelected = (template) => {
    setSelectedTemplate(template);
    setSelectedCrew(pendingCrewMember);
    setFamiliarisationMode('start');
    setShowFamiliarisationView(true);
    setPendingCrewMember(null);
  };

  // Get recommended template for a crew member
  const getRecommendedTemplate = (crewMember) => {
    const defaultTemplate = getDefaultTemplate();
    // For contractors/visitors, recommend contractor template if it exists
    const position = crewMember?.position?.toLowerCase() || '';
    if (position.includes('contractor') || position.includes('visitor')) {
      const contractorTemplate = templates.find(t => t.type === 'contractor');
      if (contractorTemplate) return contractorTemplate.id;
    }
    return defaultTemplate?.id;
  };

  // Handle PDF preview
  const handlePreviewPDF = (record, crew) => {
    const { url, filename } = previewFamiliarisationPDF(record, crew, currentVessel);
    setPdfPreview({ isOpen: true, url, filename });
  };

  const closePdfPreview = () => {
    // Revoke the blob URL to free memory
    if (pdfPreview.url) {
      URL.revokeObjectURL(pdfPreview.url);
    }
    setPdfPreview({ isOpen: false, url: null, filename: null });
  };

  // Get status color and icon
  const getStatusDisplay = (status, daysRemaining, hasInProgress = false, progressPercentage = 0) => {
    if (hasInProgress && status === 'pending') {
      return {
        color: 'text-blue-600 bg-blue-50',
        icon: Clock,
        text: `In Progress (${progressPercentage}% complete)`,
        badge: 'bg-blue-100 text-blue-800'
      };
    }
    
    switch (status) {
      case 'pending':
        return {
          color: 'text-yellow-600 bg-yellow-50',
          icon: Clock,
          text: 'Pending',
          badge: 'bg-yellow-100 text-yellow-800'
        };
      case 'overdue':
        return {
          color: 'text-red-600 bg-red-50',
          icon: AlertTriangle,
          text: `${Math.abs(daysRemaining)} days overdue`,
          badge: 'bg-red-100 text-red-800'
        };
      case 'due_soon':
        return {
          color: 'text-orange-600 bg-orange-50',
          icon: AlertTriangle,
          text: `Due in ${daysRemaining} days`,
          badge: 'bg-orange-100 text-orange-800'
        };
      case 'compliant':
        return {
          color: 'text-green-600 bg-green-50',
          icon: CheckCircle,
          text: `Valid (${daysRemaining} days remaining)`,
          badge: 'bg-green-100 text-green-800'
        };
      default:
        return {
          color: 'text-gray-600 bg-gray-50',
          icon: Clock,
          text: 'Unknown',
          badge: 'bg-gray-100 text-gray-800'
        };
    }
  };

  if (crewLoading) {
    return <div className="flex justify-center py-8"><div className="text-gray-500">Loading...</div></div>;
  }

  // Show full-screen view if active
  if (showFamiliarisationView && selectedCrew) {
    // Find the latest record for this crew member if in view mode (use state if set, otherwise lookup)
    const recordForView = selectedCrewRecord || familiarisationRecords
      .filter(record => record.crewId === selectedCrew.id)
      .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))[0] || null;

    return (
      <FamiliarisationView
        onBack={() => {
          setShowFamiliarisationView(false);
          setFamiliarisationMode(null);
          setSelectedTemplate(null);
          setSelectedCrew(null);
          setSelectedCrewRecord(null);
        }}
        crew={selectedCrew}
        onComplete={handleFamiliarisationComplete}
        viewMode={familiarisationMode === 'view'}
        initialRecord={familiarisationMode === 'view' ? recordForView : null}
        template={selectedTemplate}
      />
    );
  }

  // Show full-screen customizer if active
  if (showCustomizerView) {
    return (
      <FamiliarisationCustomizerView
        onBack={() => setShowCustomizerView(false)}
      />
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content - Crew Familiarisation List */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            {/* Header - slate-600 with search inside */}
            <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-base font-semibold text-white whitespace-nowrap">
                  Familiarisation
                </h3>

                {/* Search Input */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search crew..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-white/20 rounded-md bg-white text-slate-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                {/* Filter - custom Dropdown component */}
                <Dropdown
                  value={filterStatus}
                  onChange={setFilterStatus}
                  width="w-auto min-w-[140px]"
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'overdue', label: 'Overdue' },
                    { value: 'completed', label: 'Completed' }
                  ]}
                />
              </div>
            </div>

            <div className="divide-y divide-gray-200">
              {filteredCrew.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {searchTerm || filterStatus !== 'all' ? 'No crew members match your filters' : 'No crew members found'}
                  </p>
                </div>
              ) : (
                filteredCrew.map((crew) => {
                  const statusDisplay = getStatusDisplay(crew.familiarisationStatus, crew.daysRemaining, crew.hasInProgress, crew.progressPercentage);
                  const StatusIcon = statusDisplay.icon;
                  
                  return (
                    <div 
                      key={crew.id} 
                      className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-150 relative"
                      onClick={() => {
                        if (crew.latestRecord) {
                          // View mode - go directly to view existing record
                          setSelectedCrew(crew);
                          setSelectedCrewRecord(crew.latestRecord);
                          setFamiliarisationMode('view');
                          setShowFamiliarisationView(true);
                        } else {
                          // New mode - show template selection first
                          handleStartFamiliarisation(crew);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1">
                          <div className="flex-shrink-0">
                            <div className={`w-10 h-10 rounded-full ${statusDisplay.color} flex items-center justify-center`}>
                              <StatusIcon className="h-5 w-5" />
                            </div>
                          </div>
                          <div className="ml-4 flex-1">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-gray-900">{crew.name || crew.full_name || 'Unknown'}</p>
                              <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusDisplay.badge}`}>
                                {crew.hasInProgress ? 'In Progress' :
                                 crew.familiarisationStatus === 'pending' ? 'Pending' : 
                                 crew.familiarisationStatus === 'overdue' ? 'Overdue' :
                                 crew.familiarisationStatus === 'due_soon' ? 'Due Soon' : 'Current'}
                              </span>
                            </div>
                            <div className="flex items-center text-sm text-gray-500">
                              <span>{crew.position}</span>
                              {crew.completedDate && (
                                <>
                                  <span className="mx-2">•</span>
                                  <span>Last completed: {crew.completedDate.toLocaleDateString()}</span>
                                </>
                              )}
                              {crew.hasInProgress && crew.lastSavedProgress && (
                                <>
                                  <span className="mx-2">•</span>
                                  <span className="text-blue-600">
                                    Last saved: {new Date(crew.lastSavedProgress).toLocaleTimeString()}
                                  </span>
                                </>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{statusDisplay.text}</p>
                            
                            {/* Progress bar for in-progress familiarisation */}
                            {crew.hasInProgress && !crew.latestRecord && (
                              <div className="mt-2">
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${crew.progressPercentage}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Export button for completed records */}
                        {crew.latestRecord && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent card click
                              handlePreviewPDF(crew.latestRecord, crew);
                            }}
                            className="ml-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                            title="Preview & Export certificate"
                          >
                            <FileDown className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Vessel Requirements */}
        <FamiliarisationSidebar 
          currentVessel={currentVessel}
          vesselFamiliarisationPreview={vesselFamiliarisationPreview}
          onSettingsClick={() => setShowCustomizerView(true)}
        />
      </div>

      {/* Familiarisation Modal */}
      <FamiliarisationModal
        isOpen={showFamiliarisationModal}
        onClose={() => setShowFamiliarisationModal(false)}
        crew={selectedCrew}
        onComplete={handleFamiliarisationComplete}
        viewMode={familiarisationMode === 'view'}
      />

      {/* Settings Modal */}
      <FamiliarisationSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      {/* Customizer Modal */}
      <FamiliarisationCustomizer
        isOpen={showCustomizer}
        onClose={() => setShowCustomizer(false)}
      />

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={pdfPreview.isOpen}
        onClose={closePdfPreview}
        pdfUrl={pdfPreview.url}
        filename={pdfPreview.filename}
        title="Familiarisation Certificate Preview"
      />

      {/* Template Selection Modal (Phase 3) */}
      <TemplateSelectionModal
        isOpen={showTemplateSelection}
        onClose={() => {
          setShowTemplateSelection(false);
          setPendingCrewMember(null);
        }}
        onSelect={handleTemplateSelected}
        templates={pendingCrewMember ? getTemplatesForPosition(pendingCrewMember.position) : templates}
        crewMember={pendingCrewMember}
        recommendedTemplateId={pendingCrewMember ? getRecommendedTemplate(pendingCrewMember) : null}
      />
    </>
  );
};

export default FamiliarisationContent;